// Pure helpers shared by the edge adapter (HTMLRewriter) and the in-browser editor.

export const esc = (s: unknown): string =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export interface ImageVal { src: string; alt?: string; w?: number; h?: number; }
export interface VideoVal { kind: 'file' | 'youtube' | 'vimeo'; src: string; id?: string; poster?: string; }

export function renderVideo(v: VideoVal): string {
  if (v.kind === 'youtube' && v.id) {
    return `<iframe src="https://www.youtube-nocookie.com/embed/${esc(v.id)}" title="Video" loading="lazy" allow="accelerometer; encrypted-media; picture-in-picture" allowfullscreen referrerpolicy="strict-origin-when-cross-origin" style="position:absolute;inset:0;width:100%;height:100%;border:0"></iframe>`;
  }
  if (v.kind === 'vimeo' && v.id) {
    return `<iframe src="https://player.vimeo.com/video/${esc(v.id)}" title="Video" loading="lazy" allow="picture-in-picture" allowfullscreen style="position:absolute;inset:0;width:100%;height:100%;border:0"></iframe>`;
  }
  return `<video src="${esc(v.src)}"${v.poster ? ` poster="${esc(v.poster)}"` : ''} controls playsinline preload="metadata" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover"></video>`;
}

/** Parse a YouTube / Vimeo / direct file URL into a VideoVal. */
export function parseVideoUrl(raw: string): VideoVal | null {
  let u: URL;
  try { u = new URL(raw.trim()); } catch { return null; }
  const host = u.hostname.replace(/^www\./, '');
  let id: string | null = null;
  if (host === 'youtu.be') id = u.pathname.slice(1);
  else if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) id = u.searchParams.get('v') || /\/(embed|shorts)\/([\w-]+)/.exec(u.pathname)?.[2] || null;
  if (id && /^[\w-]{6,20}$/.test(id)) return { kind: 'youtube', id, src: `https://www.youtube.com/watch?v=${id}` };
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const m = /(\d{4,12})/.exec(u.pathname);
    if (m) return { kind: 'vimeo', id: m[1], src: `https://vimeo.com/${m[1]}` };
  }
  if (/^https?:$/.test(u.protocol) && /\.(mp4|webm)(\?|$)/i.test(u.pathname)) return { kind: 'file', src: u.toString() };
  return null;
}

export function formatDate(iso: string, locale = 'en-GB'): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
}

export const stripTags = (s: string) => s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

export function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1).replace(/\s+\S*$/, '').trim();
  return `${cut || text.slice(0, max - 1)}…`;
}

export function safeCssUrl(u: string): string {
  return u.replace(/["'()\\\s]/g, (c) => encodeURIComponent(c));
}

export const normPath = (p: string): string => {
  const x = p.replace(/\/{2,}/g, '/');
  return x.endsWith('/') ? x : x + '/';
};
